import { Injectable } from '@angular/core';
import { Commands, Search } from '@local/client-contracts';
import { KeyName } from '@local/ts-infra';
import { Shortcuts } from '@shared/services/keyboard.service';
import { generateFullPrefixedURL } from '@shared/utils';
import { getPreviewId, getPreviewName, getUrlByPreviewKey } from '../../preview/helpers/preview.utils';
import { ResultContextMenuItem } from '../../results/models/results-types';
import { PreviewType } from '../../results/models/view-filters';
import { ResultCommandBuilder, ResultCommandBuilderParams, actionToIcon } from '../../results/services/result-command-builder.service';

@Injectable({
  providedIn: 'root',
})
export class PeopleCommandBuilder extends ResultCommandBuilder {
  async build(params: PeopleCommandBuilderParams): Promise<ResultContextMenuItem[]> {
    let items = [];
    items = await super.build({
      resourceId: params.resourceId,
      open: params.open,
      isFavorite: params.isFavorite,
      copy: params?.copy,
      download: params?.download,
      preview: params?.preview,
      removeCommands: params?.removeCommands,
    });
    const shortcuts: ResultShortcuts = await this.keyboardService.loadShortcuts<ResultShortcuts>([
      '_home',
      '_people',
      '_preview',
      '_goLink',
    ]);
    if (params?.open?.url && params.preview.model) {
      const copyUrl = items.find((i) => i.id === 'copy_clipboard' && i.text === 'Copy URL');
      if (copyUrl) {
        const item = params.preview.model as Search.ResultResourceItem;
        const previewType = item?.['action']?.type as PreviewType;
        const previewUrl = getUrlByPreviewKey('p', {
          id: getPreviewId(previewType, item),
          name: getPreviewName(previewType, item),
          resourceId: item?.resource?.id,
          linkId: item?.link?.id,
        });
        const url = generateFullPrefixedURL(`/people?purl=${previewUrl}`, 'path');
        copyUrl.command.value = url;
      }
    }

    if (params?.copyEmail) {
      items.push({
        id: 'copy_email',
        text: 'Copy Email Address',
        command: { type: 'copy-clipboard', value: params?.copyEmail.value } as Commands.CopyClipboard,
        shortcut: <KeyName[]>shortcuts.copyEmail.map((k) => k.toLowerCase()),
        icon: { type: 'font-icon', value: actionToIcon['copyEmail'] },
      });
    }

    if (params?.copyPhone) {
      items.push({
        id: 'copy_phone',
        text: 'Copy Phone Number',
        command: { type: 'copy-clipboard', value: params?.copyPhone.value } as Commands.CopyClipboard,
        shortcut: <KeyName[]>shortcuts.copyPhone.map((k) => k.toLowerCase()),
        icon: { type: 'font-icon', value: actionToIcon['copyPhone'] },
      });
    }
    if (params?.dynamicCommands && !params.removeCommands?.dynamicCommands) {
      params.dynamicCommands.forEach((command) => {
        switch (command) {
          case 'remove':
            items.push({
              id: 'remove',
              text: 'Remove',
              command: { type: 'dynamicCommands', value: 'remove' } as Commands.DynamicCommand,
              icon: { type: 'font-icon', value: 'icon-delete' },
            });
            break;
          case 'add-to-collection':
            items.push({
              id: 'add_to_collection',
              text: 'Add to Collection',
              command: { type: 'add-to-collection' },
              icon: { type: 'font-icon', value: 'icon-collection' },
            });
            break;
          default:
            break;
        }
      });
    }
    return items;
  }
}
export interface PeopleCommandBuilderParams extends ResultCommandBuilderParams {
  copyEmail?: { key?: string; value?: string };
  copyPhone?: { key?: string; value?: string };
}
export interface ResultShortcuts extends Shortcuts {
  copyEmail: string[];
  copyPhone: string[];
}
